package com.essntl.uikit.components.input.time

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatNumber
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.theme.esColorScheme
import core.validation.EsInputState
import kotlin.math.min

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EsTimeInput(
    value: String,
    onValueChange: (String) -> Unit,
    onClear: () -> Unit = {},
    label: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default.copy(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false,
        ),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    // Holds the latest internal TextFieldValue state. We need to keep it to have the correct value
    // of the composition.
    var textFieldValueState by remember { mutableStateOf(TextFieldValue(text = value)) }
    // Holds the latest TextFieldValue that BasicTextField was recomposed with. We couldn't simply
    // pass `TextFieldValue(text = value)` to the CoreTextField because we need to preserve the
    // composition.
    val textFieldValue = textFieldValueState.copy(text = value)

    SideEffect {
        if (textFieldValue.selection != textFieldValueState.selection ||
            textFieldValue.composition != textFieldValueState.composition
        ) {
            textFieldValueState = textFieldValue
        }
    }
    // Last String value that either text field was recomposed with or updated in the onValueChange
    // callback. We keep track of it to prevent calling onValueChange(String) for same String when
    // CoreTextField's onValueChange is called multiple times without recomposition in between.
    var lastTextValue by remember(value) { mutableStateOf(value) }

    var isTimePickerExpanded by remember { mutableStateOf(false) }

    EsInput(
        value = textFieldValue,
        onClear = onClear,
        onValueChange = {
            var toAddSelection = 0

            val digitList =
                it.text
                    .let { text ->
                        var newText = text

                        // Handle editing of the text in the middle of the string to give better UX
                        if (
                            textFieldValue.selection.collapsed &&
                            it.selection.collapsed &&
                            it.selection.start > textFieldValue.selection.start &&
                            it.text.length > textFieldValue.text.length &&
                            it.text.length > it.selection.start
                        ) {
                            val addedTextRange = textFieldValue.selection.start..<it.selection.start

                            if (2 in addedTextRange) {
                                toAddSelection++
                            }

                            if (5 in addedTextRange) {
                                toAddSelection++
                            }

                            text
                                .slice(addedTextRange)
                                .forEachIndexed { index, c ->
                                    if (c.isDigit()) {
                                        newText =
                                            newText.removeRange(
                                                startIndex = it.selection.start + index,
                                                endIndex = it.selection.start + index + 1,
                                            )
                                        toAddSelection++
                                    }
                                }
                        }

                        newText
                    }
                    .mapNotNull { c ->
                        if (c.isDigit()) c else null
                    }

            if (digitList.isNotEmpty()) {
                if (digitList.first().toString().toInt() > 2) {
                    return@EsInput
                }
            }

            if (digitList.size >= 2) {
                val hours = digitList.slice(0..<2).joinToString("").toInt()
                if (hours > 23) {
                    return@EsInput
                }
            }

            if (digitList.size >= 3) {
                if (digitList[2].toString().toInt() > 5) {
                    return@EsInput
                }
            }

            if (digitList.size >= 4) {
                val minutes = digitList.slice(2..<4).joinToString("").toInt()
                if (minutes > 59) {
                    return@EsInput
                }
            }

            val newText =
                digitList
                    .let { list ->
                        val mList =
                            list
                                .slice(0..min(list.lastIndex, 3))
                                .toMutableList()

                        if (mList.size >= 2) mList.add(2, ':')

                        if (
                            it.text.length < lastTextValue.length &&
                            mList.lastOrNull() == ':' &&
                            lastTextValue.endsWith(':')
                        ) {
                            mList.removeAt(mList.lastIndex)
                            mList.removeAt(mList.lastIndex)
                        }

                        mList
                    }
                    .joinToString("")

            val textLengthDiff = newText.length - it.text.length

            var startSelection = it.selection.start + textLengthDiff + toAddSelection
            var endSelection = it.selection.end + textLengthDiff + toAddSelection

            if (startSelection == 2) {
                if (it.selection.min > textFieldValueState.selection.min) {
                    startSelection++
                    endSelection++
                } else {
                    startSelection--
                    endSelection--
                }
            }

            val newSelection =
                TextRange(
                    start = startSelection,
                    end = endSelection,
                )

            val newTextFieldValueState =
                TextFieldValue(
                    text = newText,
                    selection = newSelection,
                )

            textFieldValueState = newTextFieldValueState

            val stringChangedSinceLastInvocation = lastTextValue != newTextFieldValueState.text
            lastTextValue = newTextFieldValueState.text

            if (stringChangedSinceLastInvocation) {
                onValueChange(newTextFieldValueState.text)
            }
        },
        label = label,
        placeholder = "HH:mm",
        message = message,
        prefix = prefix,
        suffix = {
            EsLargeIcon(
                res = EsIcons.Timer,
                modifier =
                    Modifier
                        .clip(CircleShape)
                        .pointerHoverIcon(PointerIcon.Hand)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            enabled = enabled,
                        ) {
                            isTimePickerExpanded = true
                        },
            )
        },
        ignoreStateSuffixIcons = true,
        showClearButton = false,
        enabled = enabled,
        readOnly = readOnly,
        state = state,
        visualTransformation = visualTransformation,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        shape = shape,
        modifier = modifier,
    )

    if (isTimePickerExpanded) {
        val timePickerState =
            rememberTimePickerState(
                initialHour = value.substringBefore(":").toIntOrNull() ?: 0,
                initialMinute = value.substringAfter(":").toIntOrNull() ?: 0,
            )

        BasicAlertDialog(
            onDismissRequest = {
                isTimePickerExpanded = false
            },
            modifier = modifier.wrapContentHeight(),
        ) {
            Surface(
                shape = shape,
                color = MaterialTheme.esColorScheme.bgSecondary,
            ) {
                Column(verticalArrangement = Arrangement.SpaceBetween) {
                    TimePicker(
                        state = timePickerState,
                        colors =
                            TimePickerDefaults.colors(
                                clockDialColor = MaterialTheme.esColorScheme.bgSecondary,
                                clockDialSelectedContentColor = MaterialTheme.esColorScheme.textContrast,
                                clockDialUnselectedContentColor = MaterialTheme.esColorScheme.textPrimary,
                                selectorColor = MaterialTheme.esColorScheme.textPrimary,
                                containerColor = MaterialTheme.esColorScheme.bgSecondary,
                                periodSelectorBorderColor = MaterialTheme.esColorScheme.textPrimary,
                                periodSelectorSelectedContainerColor = MaterialTheme.esColorScheme.textPrimary,
                                periodSelectorUnselectedContainerColor = MaterialTheme.esColorScheme.bgSecondary,
                                periodSelectorSelectedContentColor = MaterialTheme.esColorScheme.textContrast,
                                periodSelectorUnselectedContentColor = MaterialTheme.esColorScheme.textPrimary,
                                timeSelectorSelectedContainerColor = MaterialTheme.esColorScheme.textPrimary,
                                timeSelectorUnselectedContainerColor = MaterialTheme.esColorScheme.bgSecondary,
                                timeSelectorSelectedContentColor = MaterialTheme.esColorScheme.textContrast,
                                timeSelectorUnselectedContentColor = MaterialTheme.esColorScheme.textPrimary,
                            ),
                        modifier =
                            Modifier
                                .padding(12.dp),
                    )

                    // Buttons
                    Box(
                        modifier =
                            Modifier
                                .align(Alignment.End)
                                .padding(bottom = 8.dp, end = 6.dp),
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(10.dp),
                        ) {
                            EsLargeSecondaryButton(
                                onClick = {
                                    isTimePickerExpanded = false
                                },
                                text = "Cancel",
                            )

                            EsLargePrimaryButton(
                                onClick = {
                                    val hour = formatNumber(timePickerState.hour)
                                    val minute = formatNumber(timePickerState.minute)
                                    onValueChange("$hour:$minute")
                                    isTimePickerExpanded = false
                                },
                                text = "Done",
                            )
                        }
                    }
                }
            }
        }
    }
}
